import {
    type DefaultObject,
    type PaginatedResponse,
} from '@moniebook-web/types';
import {
    DROPDOWN_FETCH_SIZE,
    getApiErrorMessage,
    type ErrorResponse,
} from '@moniebook-web/utils';
import { type AxiosError } from 'axios';
import Dexie, { type Collection, type IndexableType } from 'dexie';
import { Axios } from '../api';
import { REGISTERS_API_URL } from '../constants';
import { type AppDatabase } from '../db';
import { type RemoteCart } from '../models';
import { MAX_FETCH_PROGRESS, retrieveFullPaginatedData } from '../pagination';
import { useBusinessStore } from '../store/use-business-store';
import {
    REMOTE_CART_STATUS,
    REMOTE_CART_SYNC_STATUS,
    type AuditLog,
    type MappedRemoteCartResponse,
    type RemoteCartItem,
    type RemoteCartSyncStatus,
} from '../types';
import { compressAuditLogs } from './compress-audit-logs';

const LISTED_STATUSES = [
    REMOTE_CART_STATUS.SAVED,
    REMOTE_CART_STATUS.ACTIVE,
] as const;

function stripCartIdFromLog(log: AuditLog): AuditLog {
    const { cartId: _ignored, ...rest } = log as AuditLog & {
        cartId?: string;
    };
    return rest;
}

export class RemoteCartServiceV2 {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async fetchAndBulkUpsert(
        lastSyncTime?: string,
        progressCallback?: (progress: number) => void,
    ) {
        const params = {
            timestamp: lastSyncTime ?? null,
            isDeleted: lastSyncTime ? false : null,
            branchId: useBusinessStore.getState().businessInfo.branchId,
            ...(lastSyncTime ? {} : { cartStatus: REMOTE_CART_STATUS.SAVED }),
        };

        await retrieveFullPaginatedData<MappedRemoteCartResponse>(
            REGISTERS_API_URL.REGISTER.SAVED_CART,
            params,
            async (data, progress) => {
                progressCallback?.(progress);
                await this.bulkUpsert(data);
            },
        );

        progressCallback?.(MAX_FETCH_PROGRESS);
    }

    async getSavedCarts({
        createdByIds,
        excludeCreatedById,
        searchTerm,
        syncStatus,
        dateFrom,
        dateTo,
        pageSize,
        offset,
    }: {
        createdByIds?: string[];
        excludeCreatedById?: string;
        searchTerm?: string;
        syncStatus?: RemoteCartSyncStatus;
        dateFrom?: string;
        dateTo?: string;
        pageSize: number;
        offset: number;
    }): Promise<{ data: RemoteCart[]; totalElements: number }> {
        const lowerSearch = searchTerm?.toLowerCase().trim();

        const baseQuery = this.buildBaseQuery(createdByIds, dateFrom, dateTo);

        const matching = await baseQuery
            .filter((cart) => {
                if (cart.cartVersion !== 2) return false;
                if (
                    excludeCreatedById &&
                    cart.createdById === excludeCreatedById
                )
                    return false;
                if (syncStatus && cart.syncStatus !== syncStatus) return false;
                if (
                    lowerSearch &&
                    !cart.name?.toLowerCase().includes(lowerSearch)
                )
                    return false;
                if (dateFrom && cart.createdAt < dateFrom) return false;
                if (dateTo && cart.createdAt > dateTo) return false;
                return true;
            })
            .toArray();

        matching.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        return {
            totalElements: matching.length,
            data: matching.slice(offset, offset + pageSize),
        };
    }

    async getSavedCart(id: string): Promise<RemoteCart | undefined> {
        const cart = await this.db.carts.get(id);
        if (cart?.status !== REMOTE_CART_STATUS.SAVED) return undefined;

        return cart;
    }

    async getViewableCart(id: string): Promise<RemoteCart | undefined> {
        const cart = await this.db.carts.get(id);
        if (!cart?.status) return;

        const isListed = LISTED_STATUSES.some(
            (status) => status === cart.status,
        );

        return isListed ? cart : undefined;
    }

    async fetchAndUpdateAuditLogs(cartId: string): Promise<void> {
        try {
            const { data } = await Axios.get<PaginatedResponse<AuditLog>>(
                `${REGISTERS_API_URL.REGISTER.SAVED_CART}/${cartId}/audit-log`,
                { params: { size: DROPDOWN_FETCH_SIZE } },
            );

            if (!data.content) return;

            await this.db.carts.update(cartId, {
                auditLogs: data.content.map(stripCartIdFromLog),
            });
        } catch {
            // Don't update if the call fails
        }
    }

    async unsyncedUpsert(cart: RemoteCart): Promise<void> {
        const modifiedCount = await this.db.carts
            .where('id')
            .equals(cart.id)
            .modify((existing) => {
                Object.assign(existing, cart, {
                    auditLogs: cart.auditLogs ?? existing.auditLogs ?? null,
                    syncStatus: REMOTE_CART_SYNC_STATUS.PENDING,
                });
            });

        if (modifiedCount === 0) {
            await this.db.carts.add({
                ...cart,
                auditLogs: cart.auditLogs ?? null,
                syncStatus: REMOTE_CART_SYNC_STATUS.PENDING,
            });
        }
    }

    async appendAuditLog(cartId: string, log: AuditLog): Promise<void> {
        await this.db.carts
            .where('id')
            .equals(cartId)
            .modify((cart) => {
                cart.auditLogs = compressAuditLogs(cart.auditLogs ?? [], log);
                cart.syncStatus = REMOTE_CART_SYNC_STATUS.PENDING;
            });
    }

    async getUnsyncedCarts(): Promise<RemoteCart[]> {
        return this.db.carts
            .where('syncStatus')
            .equals(REMOTE_CART_SYNC_STATUS.PENDING)
            .toArray();
    }

    async bulkUpsert(carts: MappedRemoteCartResponse[]) {
        const cartIds = carts.map((c) => c.id);
        const existingCarts = await this.db.carts.bulkGet(cartIds);

        const existingById: Record<string, RemoteCart | undefined> = {};
        existingCarts.forEach((cart, index) => {
            const id = Reflect.get(cartIds, index);
            Reflect.set(existingById, id, cart);
        });

        const output = carts.map((cart) => {
            const existing = existingById[cart.id];
            const existingItemsById: Record<
                string,
                RemoteCartItem | undefined
            > = {};
            existing?.items?.forEach((item) => {
                existingItemsById[item.id] = item;
            });

            return {
                ...cart,
                items: (cart.items ?? []).map((item) => {
                    const existingItem = existingItemsById[item.id];
                    return {
                        ...item,
                        uomId: item.uomId ?? existingItem?.uomId,
                        baseUomId: item.baseUomId ?? existingItem?.baseUomId,
                        conversionRatio:
                            item.conversionRatio ??
                            existingItem?.conversionRatio,
                        pricingGroupId:
                            item.pricingGroupId ?? existingItem?.pricingGroupId,
                    };
                }),
                syncStatus: REMOTE_CART_SYNC_STATUS.SYNCED,
                cartVersion: 2,
            };
        });

        await this.db.carts.bulkPut(output);
    }

    async syncCarts(cartId: string) {
        const cart = await this.db.carts.get(cartId);

        if (!cart?.id) {
            return { success: false, error: 'Cart not found' };
        }

        if (cart.syncStatus !== REMOTE_CART_SYNC_STATUS.PENDING) {
            return { success: true, error: null };
        }

        try {
            if (cart.auditLogs?.length) {
                await Axios.put<MappedRemoteCartResponse>(
                    REGISTERS_API_URL.REGISTER.CART_UPDATE_V2,
                    {
                        ...cart,
                        auditLogs: cart.auditLogs.map(stripCartIdFromLog),
                        platform: 'WEB',
                    },
                );
            } else {
                await Axios.put<MappedRemoteCartResponse>(
                    REGISTERS_API_URL.REGISTER.CREATE_SAVED_CART,
                    cart,
                );
            }

            const shouldRetain =
                cart.status === REMOTE_CART_STATUS.ACTIVE ||
                cart.status === REMOTE_CART_STATUS.SAVED;

            if (shouldRetain) {
                await this.db.carts.update(cartId, {
                    syncStatus: REMOTE_CART_SYNC_STATUS.SYNCED,
                });
            } else {
                await this.db.carts.delete(cartId);
            }

            return { success: true, error: null };
        } catch (error) {
            const axiosError = error as AxiosError;

            const errorMessage = getApiErrorMessage(
                axiosError as unknown as Partial<ErrorResponse<DefaultObject>>,
            );

            return {
                success: false,
                error: errorMessage ?? 'Unknown error',
            };
        }
    }

    private buildBaseQuery(
        createdByIds?: string[],
        dateFrom?: string,
        dateTo?: string,
    ): Collection<RemoteCart, string> {
        // When a date range is active, use [status+createdAt] so IndexedDB
        // narrows the result set at the B-tree level instead of loading all
        // listed carts into memory. createdByIds filtering falls back to
        // in-memory in this branch.
        if (dateFrom || dateTo) {
            return this.rangeAcrossStatuses(
                '[status+createdAt]',
                dateFrom ?? Dexie.minKey,
                dateTo ?? Dexie.maxKey,
            );
        }

        // When filtering by specific staff members, use [status+createdById]
        // to let IndexedDB do the creator lookup directly.
        if (createdByIds?.length) {
            return this.db.carts
                .where('[status+createdById]')
                .anyOf(
                    createdByIds.flatMap((id) =>
                        LISTED_STATUSES.map((status) => [status, id]),
                    ),
                );
        }

        // Default: fetch all listed carts regardless of creator.
        return this.rangeAcrossStatuses(
            '[status+createdById]',
            Dexie.minKey,
            Dexie.maxKey,
        );
    }

    private rangeAcrossStatuses(
        index: string,
        lowerTail: IndexableType,
        upperTail: IndexableType,
    ): Collection<RemoteCart, string> {
        const [firstStatus, ...otherStatuses] = LISTED_STATUSES;

        let collection = this.db.carts
            .where(index)
            .between(
                [firstStatus, lowerTail],
                [firstStatus, upperTail],
                true,
                true,
            );

        otherStatuses.forEach((status) => {
            collection = collection
                .or(index)
                .between([status, lowerTail], [status, upperTail], true, true);
        });

        return collection;
    }
}
